/** Users ↔ employees ↔ login + project storage */

export const ONBOARDING_USERS_KEY = 'onboarding_users';
export const ONBOARDING_EMP_KEY = 'onboarding_employees';
export const PROJECTS_STORAGE_KEY = 'pragmr_projects_data';
export const PROJECTS_UPDATED_EVENT = 'pragmr-projects-updated';

export const normKey = (s) =>
  (s || '')
    .replace(/\*/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const keysMatch = (a, b) => {
  if (!a || !b) return false;
  return a === b || a.includes(b) || b.includes(a);
};

export const loadOnboardingUsers = () => {
  try {
    const raw = localStorage.getItem(ONBOARDING_USERS_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};

export const saveOnboardingUsers = (users) => {
  localStorage.setItem(ONBOARDING_USERS_KEY, JSON.stringify(users));
  window.dispatchEvent(new CustomEvent('onboarding-users-updated'));
};

export const loadOnboardingEmployees = () => {
  try {
    const raw = localStorage.getItem(ONBOARDING_EMP_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};

const isAdminRole = (role) => String(role || '').toLowerCase().includes('admin');

export const authUserKeys = (user) => {
  const keys = new Set();
  if (!user) return keys;
  [
    user.name,
    user.username,
    user.email,
    user.email ? String(user.email).split('@')[0] : ''
  ]
    .map(normKey)
    .filter(Boolean)
    .forEach((k) => keys.add(k));
  return keys;
};

export const findOnboardingUserForAuth = (authUser) => {
  if (!authUser) return null;
  const users = loadOnboardingUsers();
  const email = normKey(authUser.email);
  const keys = authUserKeys(authUser);

  for (const u of users) {
    if (authUser.id != null && u.authUserId != null && String(u.authUserId) === String(authUser.id)) {
      return u;
    }
    if (email && normKey(u.email) === email) return u;
    const ou = normKey(u.username);
    for (const k of keys) {
      if (keysMatch(ou, k)) return u;
    }
  }
  return null;
};

export const syncAuthUserToOnboardingUsers = (authUser) => {
  if (!authUser?.email) return loadOnboardingUsers();

  let users = loadOnboardingUsers();
  const email = (authUser.email || '').trim();
  const emailKey = normKey(email);
  const displayName = (authUser.name || email.split('@')[0] || 'User').trim();
  const roleLabel = isAdminRole(authUser.role) ? 'Admin' : 'Project User';

  let idx = users.findIndex(
    (u) =>
      (authUser.id != null && u.authUserId != null && String(u.authUserId) === String(authUser.id)) ||
      normKey(u.email) === emailKey
  );

  if (idx < 0) {
    idx = users.findIndex((u) => {
      const ou = normKey(u.username);
      for (const k of authUserKeys(authUser)) {
        if (keysMatch(ou, k)) return true;
      }
      return false;
    });
  }

  if (idx >= 0) {
    users = [...users];
    users[idx] = {
      ...users[idx],
      username: users[idx].username || displayName,
      email: email || users[idx].email,
      role: users[idx].role || roleLabel,
      authUserId: authUser.id,
      status: 'Online',
      active: users[idx].active || 'Active'
    };
  } else {
    const newId = users.length > 0 ? Math.max(...users.map((u) => Number(u.id) || 0)) + 1 : 1;
    users = [
      ...users,
      {
        id: newId,
        username: displayName,
        email,
        role: roleLabel,
        status: 'Online',
        active: 'Active',
        authUserId: authUser.id
      }
    ];
  }

  saveOnboardingUsers(users);
  return users;
};

export const getEmployeeLinkedToUser = (username, employees = loadOnboardingEmployees()) => {
  if (!username) return null;
  const key = normKey(username);
  return (
    employees.find((e) => {
      const linked = normKey(e.linkedUser);
      return linked && linked !== 'none' && linked === key;
    }) || null
  );
};

export const findEmployeeForAuthUser = (authUser) => {
  if (!authUser) return null;

  const employees = loadOnboardingEmployees();
  const obUsers = loadOnboardingUsers();
  const keys = authUserKeys(authUser);
  const obUser = findOnboardingUserForAuth(authUser);

  for (const emp of employees) {
    const linked = normKey(emp.linkedUser);
    const en = normKey(emp.name);

    if (linked && linked !== 'none') {
      for (const k of keys) {
        if (keysMatch(linked, k)) return emp;
      }

      const linkedOb = obUsers.find(
        (u) => u.username === emp.linkedUser || normKey(u.username) === linked
      );
      if (linkedOb) {
        if (
          authUser.id != null &&
          linkedOb.authUserId != null &&
          String(linkedOb.authUserId) === String(authUser.id)
        ) {
          return emp;
        }
        const ou = normKey(linkedOb.username);
        const oe = normKey(linkedOb.email);
        for (const k of keys) {
          if (keysMatch(ou, k) || keysMatch(oe, k)) return emp;
        }
      }
    }

    if (obUser && emp.linkedUser === obUser.username) return emp;

    for (const k of keys) {
      if (keysMatch(en, k)) return emp;
    }
    if (emp.email && keys.has(normKey(emp.email))) return emp;
  }

  return null;
};

export const resolveEmployeeIdForAuthUser = (authUser) => {
  const emp = findEmployeeForAuthUser(authUser);
  if (emp) return emp.id;
  return authUser?.id ?? null;
};

export const saveProjectsStore = (store) => {
  localStorage.setItem(PROJECTS_STORAGE_KEY, JSON.stringify(store));
  window.dispatchEvent(new CustomEvent(PROJECTS_UPDATED_EVENT));
};

export const loadProjectsStore = () => {
  try {
    const raw = localStorage.getItem(PROJECTS_STORAGE_KEY);
    if (!raw) return { projects: [], members: {}, assignments: {}, nextId: 1 };
    const parsed = JSON.parse(raw);
    return {
      projects: parsed.projects || [],
      members: parsed.members || {},
      assignments: parsed.assignments || {},
      nextId: parsed.nextId || 1
    };
  } catch {
    return { projects: [], members: {}, assignments: {}, nextId: 1 };
  }
};

export const loadProjectsList = () => loadProjectsStore().projects;

/** Match logged-in user to Employee Onboarding row */
export const resolveEmployeeIdForUser = (user) => resolveEmployeeIdForAuthUser(user);

/** Project ids this user owns via assignment or project membership */
export const getUserProjectIds = (user) => {
  const ids = new Set();
  if (!user) return ids;

  const store = loadProjectsStore();
  const empId = resolveEmployeeIdForUser(user);
  const assignedId = getAssignedProjectIdForUser(user);
  if (assignedId) ids.add(String(assignedId));

  if (empId != null) {
    Object.entries(store.members || {}).forEach(([pid, members]) => {
      if (
        Array.isArray(members) &&
        members.some((m) => String(m.id) === String(empId))
      ) {
        ids.add(String(pid));
      }
    });
  }

  return ids;
};

const taskInUserProjects = (task, user) => {
  const projectIds = getUserProjectIds(user);
  if (projectIds.size === 0) return false;
  if (projectIds.has(String(task.project_id))) return true;

  const store = loadProjectsStore();
  const taskProjName = (task.project_name || '').trim().toLowerCase();
  if (!taskProjName) return false;
  for (const pid of projectIds) {
    const p = store.projects.find((pr) => String(pr.id) === String(pid));
    if (p && (p.name || '').trim().toLowerCase() === taskProjName) return true;
  }
  return false;
};

export const getAssignedProjectIdForUser = (user) => {
  if (!user) return null;
  const store = loadProjectsStore();
  const { assignments } = store;
  const empId = resolveEmployeeIdForUser(user);

  if (empId != null && assignments[String(empId)]) {
    return assignments[String(empId)];
  }
  if (assignments[String(user.id)]) {
    return assignments[String(user.id)];
  }

  const keys = authUserKeys(user);
  for (const emp of loadOnboardingEmployees()) {
    const en = normKey(emp.name);
    const linked = normKey(emp.linkedUser);
    let nameMatch = false;
    for (const k of keys) {
      if (keysMatch(en, k) || keysMatch(linked, k)) {
        nameMatch = true;
        break;
      }
    }
    if (nameMatch && assignments[String(emp.id)]) {
      return assignments[String(emp.id)];
    }
  }
  return null;
};

export const getGloballyAssignedProjectIds = () => {
  const { assignments } = loadProjectsStore();
  return new Set(
    Object.values(assignments || {})
      .filter(Boolean)
      .map(String)
  );
};

export const sortProjectsWithAssignedFirst = (projects, assignedId) => {
  if (!assignedId) return [...projects];
  const aid = String(assignedId);
  return [...projects].sort((a, b) => {
    const aFirst = String(a.id) === aid;
    const bFirst = String(b.id) === aid;
    if (aFirst && !bFirst) return -1;
    if (!aFirst && bFirst) return 1;
    return (a.name || '').localeCompare(b.name || '');
  });
};

/** Employees added under Project Onboarding → Membership for this project */
export const getProjectMembers = (projectId) => {
  if (projectId == null || projectId === '' || String(projectId) === 'all') return [];
  return loadProjectsStore().members[String(projectId)] || [];
};

export const isEmployeeMemberOfProject = (employeeId, projectId) => {
  if (employeeId == null || employeeId === '') return false;
  const pid = projectId != null ? String(projectId) : '';
  if (!pid || pid === 'all') return false;
  return getProjectMembers(pid).some((m) => String(m.id) === String(employeeId));
};

/** Assignee dropdown / Smart Assign — project members only */
export const listEmployeesForProjectAssignment = (projectId) => {
  const pid = projectId != null ? String(projectId) : '';
  if (!pid || pid === 'all') return [];
  const memberIds = new Set(getProjectMembers(pid).map((m) => String(m.id)));
  return loadOnboardingEmployees().filter((e) => memberIds.has(String(e.id)));
};

export const validateProjectAssignment = (employeeId, projectId) => {
  const pid = projectId != null ? String(projectId) : '';
  if (!pid || pid === 'all') {
    return { ok: false, message: 'This task has no project — assign after it is linked to a project.' };
  }
  const members = getProjectMembers(pid);
  if (members.length === 0) {
    return {
      ok: false,
      message:
        'No members on this project. Add employees in Admin → Project Onboarding → Membership.'
    };
  }
  if (!employeeId) return { ok: true };
  if (!isEmployeeMemberOfProject(employeeId, pid)) {
    return {
      ok: false,
      message: 'Only employees in this project membership can be assigned.'
    };
  }
  return { ok: true };
};

export const sortProjectsForTopBar = (projects, userAssignedId) => {
  const globalAssigned = getGloballyAssignedProjectIds();
  return [...projects].sort((a, b) => {
    const aUser = userAssignedId && String(a.id) === String(userAssignedId);
    const bUser = userAssignedId && String(b.id) === String(userAssignedId);
    const aGlob = globalAssigned.has(String(a.id));
    const bGlob = globalAssigned.has(String(b.id));
    if (aUser && !bUser) return -1;
    if (!aUser && bUser) return 1;
    if (aGlob && !bGlob) return -1;
    if (!aGlob && bGlob) return 1;
    return (a.name || '').localeCompare(b.name || '');
  });
};
